import { EXCLUSIVE_RAILS_HOST } from 'components/globals';
import { getListingDetails } from 'components/offerPortal/getListingDetails';
import { isInRenovation, type ListingDetails } from 'components/offerPortal/listingDetails';
import ListingInRenovation from 'components/offerPortal/ListingInRenovation';
import OfferConfirmationPage from 'components/offerPortal/OfferConfirmation';
import OfferPortalLanding from 'components/offerPortal/OfferPortalLanding';
import OfferWizard from 'components/offerPortal/OfferWizard';
import { resolvePortalRoute } from 'components/offerPortal/portalPages';
import { isTexasListing } from 'components/offerPortal/texasRollout';
import StaticPageLayout from 'components/static-site/StaticPageLayout';
import {
  createStaticPageProps,
  staticPageFields,
  type StaticPageProps,
} from 'components/static-site/staticPageProps';
import type { CacheSafeGetServerSideProps } from 'declarations/shared/serverSideProps';

/**
 * Offer Portal route shell. `[[...page]]` is Next's optional catch-all, so
 * this one page owns the whole subtree — `/make-offer`, `/make-offer/start`,
 * `/make-offer/<slug>` — matching the legacy `/:page?` shape;
 * `resolvePortalRoute` dispatches on the segments.
 *
 * The public URL has to stay `www.opendoor.com/make-offer/…` because MLS
 * remarks on live listings print it. A later PR adds the Cloudflare Worker
 * route that proxies the path here; until then `/make-offer` still reaches the
 * legacy Rails portal and these pages are served only from the Cosmos origin.
 * The cutover must also 301 the three alias spellings the legacy router
 * answers on (`/makeoffer`, `/makeoffers`, `/make-offers` — `routes.rb`) to
 * `/make-offer`; an alias left unclaimed keeps serving the legacy portal.
 */
const portalPageFields = staticPageFields(
  'make-offer/start',
  'Submit an offer for your client | Opendoor',
  'Submit your client’s offer on an Opendoor home in minutes, with full visibility from offer to close.',
);

type OfferPortalProps = StaticPageProps &
  (
    | { view: 'landing' }
    | { view: 'confirmation' }
    | { view: 'renovation'; listing: ListingDetails }
    | { view: 'offerTerms'; listing: ListingDetails; slug: string }
  );

/**
 * On the primary public host `/make-offer` is still Rails-owned, but having
 * this page in the build manifest lets the Next client router reach it there
 * through the `/_next/data` ingress path (e.g. a same-origin link on
 * `/checkout`). Refuse to serve it from that host so the funnel stays dark
 * until the Worker proxies the path here; the Worker-cutover PR removes this
 * gate along with its route change.
 */
const railsPortalHost = new URL(EXCLUSIVE_RAILS_HOST).host;

export const getServerSideProps: CacheSafeGetServerSideProps<OfferPortalProps> = async (
  context,
) => {
  if (context.req.headers.host === railsPortalHost) {
    return { notFound: true };
  }

  const route = resolvePortalRoute(context.params?.page);

  if (route.kind === 'redirect') {
    // Keep the query string (MLS/ad attribution params) across the redirect.
    // Everything after the first `?` is the query — RFC 3986 allows further
    // literal `?` characters inside it, so split('?')[1] would truncate.
    const queryStart = context.resolvedUrl.indexOf('?');
    const search = queryStart === -1 ? '' : context.resolvedUrl.slice(queryStart + 1);
    return {
      redirect: {
        destination: search ? `${route.destination}?${search}` : route.destination,
        permanent: false,
      },
    };
  }
  if (route.kind === 'notFound') {
    return { notFound: true };
  }
  if (route.kind === 'landing') {
    return { props: { ...createStaticPageProps(portalPageFields), view: 'landing' } };
  }
  if (route.kind === 'confirmation') {
    // Everything the page shows comes from the client-side submission
    // handoff; there is nothing to fetch by URL.
    return { props: { ...createStaticPageProps(portalPageFields), view: 'confirmation' } };
  }

  const listing = await getListingDetails(route.slug);
  if (!listing) {
    return { notFound: true };
  }
  // The Worker keeps non-Texas slugs on the legacy portal by parsing the
  // path; this is the authoritative check on the listing's own address.
  if (!isTexasListing(listing)) {
    return { notFound: true };
  }
  if (isInRenovation(listing)) {
    return { props: { ...createStaticPageProps(portalPageFields), view: 'renovation', listing } };
  }

  // Everything that is not in renovation falls through to the wizard for
  // now. Legacy also gated in-contract states (resale_in_contract,
  // resale_contingencies_released) with a backup-offer notice; those pages
  // follow once designs exist.
  return {
    props: {
      ...createStaticPageProps(portalPageFields),
      view: 'offerTerms',
      listing,
      slug: route.slug,
    },
  };
};

const portalView = (props: OfferPortalProps) => {
  switch (props.view) {
    case 'renovation':
      return <ListingInRenovation listing={props.listing} />;
    case 'offerTerms':
      return <OfferWizard listing={props.listing} slug={props.slug} />;
    case 'confirmation':
      return <OfferConfirmationPage />;
    default:
      return <OfferPortalLanding />;
  }
};

const OfferPortal = (props: OfferPortalProps) => (
  <StaticPageLayout
    {...props}
    // The wizard and confirmation sit on the primary ground, so their nav
    // stays white; the landing and notice pages tint it to their secondary
    // ground.
    headerClassName={
      props.view === 'offerTerms' || props.view === 'confirmation'
        ? undefined
        : 'bg-od-bg-secondary'
    }
  >
    {portalView(props)}
  </StaticPageLayout>
);

export default OfferPortal;
